import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';
import {
  ApiError,
  getClosedOrLockedErrorMessage,
  getAttemptErrorMessage,
  ErrorWithData,
  getAuthorizationErrorMessage,
} from '@blocs.auth';
import { useHistory } from 'react-router-dom';
import Alert from '@minecraft.alert';
import UncontrolledAlert from '@minecraft.uncontrolled-alert';
import { constants } from '@minecraft.utils';
import { TranslateFn, Trans, useTranslation } from '@blocs.i18n';
import { getIntlErrorMessage, IntlErrorPrefix } from 'utils/hooks/errorMessage';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';

const InstanceLink: FC<{ link: string; label: string }> = ({ link, label }: { link: string; label: string }) => {
  return <a href={`//login.${link}`}>{label}</a>;
};

interface GetMsgInputs {
  t: TranslateFn;
  error?: ApiError;
  isLegacyOnboardingDeprecated: boolean;
}

interface GetMsgResult {
  message?: React.ReactNode;
  color?: 'warning' | 'danger' | 'success';
}

const getMsgFromErr = ({ t, error, isLegacyOnboardingDeprecated }: GetMsgInputs): GetMsgResult => {
  const graphqlErr: ErrorWithData = error?.graphQLErrors?.[0];
  const code = graphqlErr?.data?.errorCode;

  switch (code) {
    case 29:
      return { message: getIntlErrorMessage(t, graphqlErr, IntlErrorPrefix.LOGIN), color: 'warning' };
    case 20: {
      // this is an error code for user that has can't be onboarded due restricted instance
      const { instance } = graphqlErr.data.errorData;
      const instanceInfo = constants.instanceConstants.find((i) => i.value === instance);

      return {
        message: (
          <span data-testid="login-restrictedinstancelink">
            <Trans
              i18nKey="login:login.error.restrictedInstanceError"
              values={{ error: graphqlErr.message }}
              components={{
                break: <br />,
                link: <InstanceLink link={instanceInfo.link} label={instanceInfo.label} />,
              }}
            />
          </span>
        ),
        color: 'warning',
      };
    }
    case 21:
      // this is an error code for user that has a locked or closed account
      return { message: getClosedOrLockedErrorMessage(t, graphqlErr), color: 'danger' };
    case 1: // internal error with embedded account locked (too many attempts)
    case 4: // account not found error
    case 9: // login attempts error
      return { message: getAttemptErrorMessage(t, graphqlErr, isLegacyOnboardingDeprecated), color: 'warning' };
    case 30: // Legacy Onboarding Deprecated
      return { message: t('login:login.error.legacyOnboardingDeprecated'), color: 'danger' };
    case 5: {
      const message = getAuthorizationErrorMessage(t, graphqlErr);
      return { message, color: 'danger' };
    }
    default:
      return { message: getIntlErrorMessage(t, graphqlErr, IntlErrorPrefix.LOGIN), color: 'warning' };
  }
};

interface LoginAlertsProps {
  error?: ApolloError;
}

const LoginAlertMessage: FC<LoginAlertsProps> = ({ error }) => {
  const { t } = useTranslation();
  const isLegacyOnboardingDeprecated = useFeatureExperiment(FEATURE_FLAGS.WEB_ONBOARDING_DEPRECATION);
  const history = useHistory();
  const search = history?.location?.search;
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const emailVerified = params.get('emailVerified');
  const rosterInvite = params.get('rosterInvite');
  const onBoardingError = params.get('onBoardingError');
  const [loginErrMsg, setLoginErrMsg] = useState<GetMsgResult>(
    onBoardingError ? { message: onBoardingError, color: 'danger' } : null
  );

  const removeQueryParam = useCallback(
    (param: string) => {
      params.delete(param);
      history.replace({
        search: params.toString(),
      });
    },
    [history, params]
  );

  useEffect(() => {
    if (error) {
      setLoginErrMsg(getMsgFromErr({ t, error, isLegacyOnboardingDeprecated }));
    }
  }, [error, t]);

  return (
    <>
      {emailVerified && (
        <Alert
          data-qa-id="email-verification-success-message"
          sticky
          size="fullWidth"
          type="success"
          onClose={(): void => removeQueryParam('emailVerified')}
        >
          {emailVerified === 'success'
            ? t('login:inviteVerify.emailVerified')
            : t('login:inviteVerify.alreadyVerified')}
        </Alert>
      )}
      {rosterInvite && (
        <Alert
          data-qa-id="roster-invitation-message"
          sticky
          size="fullWidth"
          type="warning"
          onClose={(): void => removeQueryParam('rosterInvite')}
        >
          {rosterInvite === 'expired'
            ? t('login:inviteVerify.rosterInviteExpired')
            : t('login:inviteVerify.rosterInviteCompleted')}
        </Alert>
      )}
      {loginErrMsg && (
        <UncontrolledAlert isOpen color={loginErrMsg.color} toggle={() => setLoginErrMsg(null)}>
          {loginErrMsg.message}
        </UncontrolledAlert>
      )}
    </>
  );
};

export default LoginAlertMessage;
